import { useEffect, useMemo, useState } from 'react';
import { Model } from '../services';
import { FILTER_TYPES, computeFilteredModels, useModelStore } from '../../../stores/modelStore';

/** Pill de filtro da lista de modelos: o bucket que ela seleciona e o rótulo exibido. */
export interface ModelFilterPill {
  filter: FILTER_TYPES;
  label: string;
}

/** Pill fixa: "Todos" abre a lista de filtros e nunca é escondida. */
const ALL_PILL: ModelFilterPill = { filter: FILTER_TYPES.ALL, label: 'Todos' };

/**
 * Pills por categoria, na ordem de exibição.
 *
 * Os predicados espelham os buckets de `computeGroupedModels` — patrocinado vence
 * institucional, que vence pessoal. Divergir daqui faria a pill aparecer com a lista vazia.
 */
const CATEGORY_PILLS: Array<ModelFilterPill & { matches: (model: Model) => boolean }> = [
  {
    filter: FILTER_TYPES.HIGHLIGHTED,
    label: 'Sugestão',
    matches: (model) => Boolean(model.sponsored),
  },
  {
    filter: FILTER_TYPES.INSTITUTIONAL,
    label: 'Institucional',
    matches: (model) => !model.sponsored && model.origin === 'institutional',
  },
  {
    filter: FILTER_TYPES.PERSONAL,
    label: 'Meus modelos',
    matches: (model) => !model.sponsored && model.origin !== 'institutional',
  },
];

/**
 * Resolve as pills de filtro que devem aparecer para a lista recebida.
 *
 * Só as categorias são dinâmicas: cada uma aparece quando existe modelo dela. É o que retira
 * "Institucional" de quem não tem modelo institucional — o caso do B2C, cujo parceiro não tem
 * esses modelos vinculados. "Todos" é fixa e vem sempre, mesmo sem modelo nenhum.
 *
 * Função pura. Quem chama escolhe o recorte que define as pills; `useModelFilterPills` passa a
 * lista sem termo de busca, e é essa a combinação usada nas telas.
 *
 * @param models Modelos já mesclados (listagem principal + patrocinados).
 * @returns As pills visíveis, na ordem de exibição, com "Todos" à frente. Nunca vazio.
 */
export function resolveModelFilterPills(models: Model[]): ModelFilterPill[] {
  const categories = CATEGORY_PILLS.filter((pill) => models.some(pill.matches));

  return [ALL_PILL, ...categories.map(({ filter, label }) => ({ filter, label }))];
}

/**
 * Pills de filtro da lista de modelos, estáveis durante a busca.
 *
 * As pills refletem o que o prescritor tem, não o que a busca devolveu: digitar um termo não
 * pode fazer filtro aparecer e sumir debaixo do dedo. Por isso o recálculo só acontece quando a
 * lista carregada está sem termo — `modelsName === undefined`, que o store grava junto com os
 * `models`, é exatamente esse sinal.
 *
 * Não é um congelamento no primeiro carregamento: criar o primeiro modelo pessoal ou apagar o
 * último institucional recarrega a lista sem termo e atualiza as pills, como esperado.
 *
 * @param models Modelos da listagem principal, como vêm do store.
 * @param sponsoredModels Modelos patrocinados, vindos de endpoint separado.
 * @returns As pills visíveis, na ordem de exibição. Nunca vazio.
 */
export function useModelFilterPills(models: Model[], sponsoredModels: Model[]): ModelFilterPill[] {
  const modelsName = useModelStore((store) => store.modelsName);
  const [unsearchedModels, setUnsearchedModels] = useState<Model[]>([]);

  useEffect(() => {
    if (modelsName !== undefined) return;
    setUnsearchedModels(computeFilteredModels(models, sponsoredModels, ''));
  }, [modelsName, models, sponsoredModels]);

  return useMemo(() => resolveModelFilterPills(unsearchedModels), [unsearchedModels]);
}
